/**
 * Ядро подсветки поля с ошибкой валидации: класс `k-input-validation-error`, `aria-invalid` и снятие
 * подсветки по `change`; каналы (blur через Kendo и серверный `ModelState`) сходятся в этом ядре.
 */

import { isJQueryInstance } from './env';

/**
 * Класс красной подсветки поля: тот же, что у серверных ошибок, и тот же, что рисует `site.scss`.
 * Инлайновая «пилюля» скрыта стилями, поэтому единственная видимая метка ошибки у поля — этот класс и `aria-invalid`.
 */
const FIELD_ERROR_HIGHLIGHT_CLASS = 'k-input-validation-error';
const FIELD_ERROR_WRAPPER_SELECTOR = '.k-input, .k-picker, .k-widget';
/** Событие (с неймспейсом), по которому подсветка снимается так же, как у серверных ошибок. */
const FIELD_HIGHLIGHT_CHANGE_EVENT = 'change.validationPopupHighlight';
const ARIA_INVALID_ATTRIBUTE = 'aria-invalid';
const FIELD_ERROR_HIGHLIGHT_SELECTOR = `.${FIELD_ERROR_HIGHLIGHT_CLASS}, [${ARIA_INVALID_ATTRIBUTE}]`;
/** Контейнеры «пилюли», а не полей: `k-form-error` — errorTemplate валидатора, `k-invalid-msg` — `_decorateMessageContainer`. */
const KENDO_MESSAGE_CONTAINER_SELECTOR = '.k-invalid-msg, .k-form-error';

function resolveFieldWrapper(input: JQuery<Element>): JQuery {
    const widgetWrapper = input.closest(FIELD_ERROR_WRAPPER_SELECTOR);

    return widgetWrapper.length ? widgetWrapper : input.parent();
}

/**
 * Нормализует аргумент `validateInput` к jQuery-объекту поля: Kendo объявляет ОБЕ формы входа — `Element`
 * и `JQuery` — и зовёт метод с тем, что пришло от виджета; без этого на `Element` подсветка не применилась бы.
 * Экспорт внутренний для каталога: этим ядром пользуется канал blur в `override.ts`.
 */
export function resolveFieldInput(input: unknown): JQuery<Element> | undefined {
    if (isJQueryInstance(input)) {
        return input.length ? input : undefined;
    }

    if (!input) {
        return undefined;
    }

    const field = $(input as Element);

    return field.length ? field : undefined;
}

/** Пометка ошибки поля: принадлежит ПОЛЮ, а не каналу, который её поставил (см. `fieldErrorMarks`); класс + `aria-invalid`. */
interface FieldErrorMark {
    wrappers: JQuery;
    /** Поля, которым ставится `aria-invalid` и вешается снятие по `change`. */
    fields: JQuery<Element>;
    /** Вешать ли класс также на сами поля (нужно каналу blur: обёртка intl-tel-input своего бордера не имеет). */
    markFields: boolean;
}

/**
 * Одна пометка на поле, а не по одной на канал: поле у каналов одно, а обёртки разные (`resolveFieldWrapper`
 * ищет контейнер вверх от поля, серверный канал берёт `prev()` от `data-valmsg-for`), поэтому со своими
 * пометками снятие по `change` очистило бы только своё, а чужой контейнер остался бы помеченным навсегда
 * (та самая застрявшая красная рамка). Канал переиспользует контейнеры прежней (`adoptFieldErrorMarks`).
 * `WeakMap`, а не `Map`: ключи — живые DOM-узлы, которые пересоздаются (правка в grid popup).
 */
const fieldErrorMarks = new WeakMap<Element, FieldErrorMark>();

/**
 * Снимает пометку целиком — контейнеры, поля, атрибут и обработчик снятия: одна точка на оба канала.
 * Нужны оба набора: контейнеры из ЗАХВАЧЕННОГО при установке набора (`mark.wrappers`), не совпадающего
 * с обёртками полей (серверный канал находит их по разметке сообщения), плюс каждое поле по ПЕРЕВЫЧИСЛЕННОЙ
 * обёртке — ни один помеченный узел не остаётся помеченным. Запись о владении снимается только под ИМЕННО этот `mark`.
 */
function clearFieldErrorMark(mark: FieldErrorMark): void {
    mark.wrappers.removeClass(FIELD_ERROR_HIGHLIGHT_CLASS);

    mark.fields.each((_index, element) => {
        if (fieldErrorMarks.get(element) === mark) {
            fieldErrorMarks.delete(element);
        }

        clearFieldErrorHighlight($(element));
    });
}

/**
 * Точка сходимости каналов: принимает поля новой пометки у прежних владельцев ЦЕЛИКОМ — их контейнеры
 * переходят в новую пометку (без дублей), поэтому снятие очищает ВСЁ помеченное этим полем. Принятые
 * пометки вызывающий снимает вместе с их обработчиками `change` — иначе остался бы обработчик без пометки.
 */
function adoptFieldErrorMarks(mark: FieldErrorMark): FieldErrorMark[] {
    const adoptedMarks: FieldErrorMark[] = [];

    mark.fields.each((_index, element) => {
        const ownerMark = fieldErrorMarks.get(element);

        if (ownerMark && ownerMark !== mark && !adoptedMarks.includes(ownerMark)) {
            adoptedMarks.push(ownerMark);
        }
    });

    for (const adoptedMark of adoptedMarks) {
        mark.wrappers = mark.wrappers.add(adoptedMark.wrappers);
    }

    return adoptedMarks;
}

/**
 * Ставит пометку, всегда снимая прежние пометки тех же полей (`off` перед `on`) — обработчик снятия не
 * размножается, а при `hasError === false` снятие и есть весь смысл вызова. Снятие до установки убирает
 * и принятые (`adoptFieldErrorMarks`); класс на полях возвращается, если его ставила одна из них (blur):
 * иначе серверный канал (`markFields: false`) снял бы подсветку blur и `aria-invalid` разошёлся бы с классом.
 */
function setFieldErrorMark(mark: FieldErrorMark, hasError: boolean): void {
    const adoptedMarks = adoptFieldErrorMarks(mark);
    const keepsFieldClass = adoptedMarks.some((adoptedMark) => adoptedMark.markFields);

    clearFieldErrorMark(mark);

    for (const adoptedMark of adoptedMarks) {
        clearFieldErrorMark(adoptedMark);
    }

    if (!hasError) {
        return;
    }

    mark.wrappers.addClass(FIELD_ERROR_HIGHLIGHT_CLASS);

    if (mark.markFields || keepsFieldClass) {
        mark.fields.addClass(FIELD_ERROR_HIGHLIGHT_CLASS);
    }

    mark.fields.attr(ARIA_INVALID_ATTRIBUTE, 'true');
    mark.fields.on(FIELD_HIGHLIGHT_CHANGE_EVENT, () => {
        clearFieldErrorMark(mark);
    });

    mark.fields.each((_index, element) => {
        fieldErrorMarks.set(element, mark);
    });
}

/**
 * Снимает с поля класс, `aria-invalid` и обработчик `change` — нижняя точка очистки, поэтому они не
 * расходятся между путями. Обёртка перевычисляется ЗДЕСЬ, а не берётся из пометки: иначе не очистился бы
 * узел, который был обёрткой в момент установки пометки, а сейчас ею не является.
 */
function clearFieldErrorHighlight(field: JQuery<Element>): void {
    resolveFieldWrapper(field).removeClass(FIELD_ERROR_HIGHLIGHT_CLASS);
    field.removeClass(FIELD_ERROR_HIGHLIGHT_CLASS);
    field.removeAttr(ARIA_INVALID_ATTRIBUTE);
    field.off(FIELD_HIGHLIGHT_CHANGE_EVENT);
}

/**
 * Красная подсветка поля — канал blur (`validateInput`); тонкая обёртка над общим ядром `setFieldErrorMark`,
 * которое использует и серверный канал, поэтому класс, атрибут и точка снятия по `change` у каналов одни
 * и те же. Класс вешается и на обёртку, и на само поле (`markFields: true`): у поля внутри `.iti`
 * (intl-tel-input) обёртка своего бордера не имеет, а текста ошибки на blur нет — подсветка на обёртке была бы невидимой.
 */
export function applyFieldErrorHighlight(field: JQuery<Element>, hasError: boolean): void {
    setFieldErrorMark({ wrappers: resolveFieldWrapper(field), fields: field, markFields: true }, hasError);
}

/**
 * Подсветка поля с серверной ошибкой (`ModelState`) по имени из payload: имя приходит ровно как есть
 * (префикс окна клеит `checkValidationErrors`), лукап документ-широкий — сервер знает только имя, а поля
 * с одинаковым именем бывают в двух открытых окнах. Сужать лукап до серверного span нельзя: Kendo
 * подменяет его контейнером «пилюли», не достижимым от поля ВВЕРХ, — пометка была бы неснимаемой.
 * Набор полей включает сам контейнер, если он `<input>`: `find()` берёт только потомков, а «пилюля» стоит
 * прямо за полем — иначе класс нечем снять и `aria-invalid` не выставится; красим контейнеры, но не сами поля.
 */
export function highlightFieldWithErrorByName(name: string): void {
    const containers = $(document)
        .find(`[data-valmsg-for="${name}"],[data-val-msg-for="${name}"]`)
        .prev()
        .not(KENDO_MESSAGE_CONTAINER_SELECTOR);

    if (!containers.length) {
        return;
    }

    setFieldErrorMark(
        {
            wrappers: containers,
            fields: containers.find('input').add(containers.filter('input')),
            markFields: false
        },
        true
    );
}

/**
 * Снимает подсветку со всех полей внутри контейнера валидатора, включая сам контейнер. Нужна после
 * `validator.reset()`: Kendo убирает свою разметку ошибок, а наш класс и `aria-invalid` остаются, и на blur
 * устаревшая рамка была бы единственным и при этом неверным сигналом пользователю (путь очистки grid popup
 * editing). Контейнер входит в выборку, потому что валидатор может быть навешен прямо на поле
 * (`element.kendoValidator(...)`) — тогда `this.element` И ЕСТЬ подсвеченное поле; без jQuery очистка пропускается молча.
 */
export function clearFieldErrorHighlights(container: JQuery): void {
    const highlightedElements = container.find(FIELD_ERROR_HIGHLIGHT_SELECTOR).add(container.filter(FIELD_ERROR_HIGHLIGHT_SELECTOR));

    highlightedElements.each((_index, element) => {
        clearFieldErrorHighlight($(element));
    });
}
